// src/components/PressureChart/PressureChart.js
import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import './PressureChart.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const PressureChart = ({ historicalData, footSide }) => {
  const [selectedChart, setSelectedChart] = useState(0);
  const [chartData, setChartData] = useState({ datasets: [] });
  const maxDataPoints = 100;

  const chartTypes = [
    { 
      name: 'Front vs Back',
      // Keys matching the sectionAverages object
      dataKeys: ['front', 'back'],
      labels: ['Front', 'Back'],
      colors: ['#FF6B6B', '#4ECDC4']
    },
    { 
      name: 'Left vs Right',
      dataKeys: ['leftTotal', 'rightTotal'],
      labels: ['Left', 'Right'],
      colors: ['#95E1D3', '#F38181']
    },
    { 
      name: 'Platform vs Metatarsal',
      dataKeys: ['platform', 'metatarsal'],
      labels: ['Platform', 'Metatarsal'],
      colors: ['#A8E6CF', '#FFD3B6']
    }
  ];

  useEffect(() => {
    if (!historicalData || historicalData.length === 0) {
      setChartData({ datasets: [] });
      return;
    }

    const currentChartType = chartTypes[selectedChart];
    const timestamps = [];
    const category1Data = [];
    const category2Data = [];

    // Process historical data - use pre-calculated sectionAverages
    const recentData = historicalData.slice(-maxDataPoints);
    
    recentData.forEach(dataPoint => {
      // Use sectionAverages if available, otherwise skip
      if (dataPoint.sectionAverages) {
        timestamps.push(new Date(dataPoint.timestamp).toLocaleTimeString());
        
        // Get values directly from pre-calculated sectionAverages
        const key1 = currentChartType.dataKeys[0];
        const key2 = currentChartType.dataKeys[1];
        
        category1Data.push(dataPoint.sectionAverages[key1] || 0);
        category2Data.push(dataPoint.sectionAverages[key2] || 0);
      }
    });

    // Only set chart data if we have valid data points
    if (timestamps.length === 0) {
      setChartData({ datasets: [] });
      return;
    }

    setChartData({
      labels: timestamps,
      datasets: [
        {
          label: currentChartType.labels[0],
          data: category1Data,
          borderColor: currentChartType.colors[0],
          backgroundColor: `${currentChartType.colors[0]}20`,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
        {
          label: currentChartType.labels[1],
          data: category2Data,
          borderColor: currentChartType.colors[1],
          backgroundColor: `${currentChartType.colors[1]}20`,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4,
        }
      ]
    });
  }, [historicalData, selectedChart, chartTypes]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: `${footSide.charAt(0).toUpperCase() + footSide.slice(1)} Foot - ${chartTypes[selectedChart].name}`,
        font: {
          size: 14,
          weight: 'bold'
        },
        padding: {
          bottom: 10
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(0)}`;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Time',
          font: {
            size: 12
          }
        },
        ticks: {
          display: false,
          maxTicksLimit: 10,
          maxRotation: 45,
          minRotation: 45
        },
        grid: {
          display: false
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Average Pressure',
          font: {
            size: 12
          }
        },
        min: 0,
        ticks: {
          display: false,
          stepSize: 500
        },
        grid: {
          display: false
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  return (
    <div className="pressure-chart-container">
      <div className="chart-selector">
        {chartTypes.map((type, index) => (
          <button
            key={index}
            className={`chart-dot ${selectedChart === index ? 'active' : ''}`}
            onClick={() => setSelectedChart(index)}
            aria-label={type.name}
            title={type.name}
          />
        ))}
      </div>
      <div className="chart-wrapper">
        {chartData.datasets.length > 0 ? (
          <Line data={chartData} options={options} />
        ) : (
          <div className="no-data-message">
            Waiting for pressure data...
          </div>
        )}
      </div>
    </div>
  );
};

export default PressureChart;